import { ReportRecordFields } from '../../graphql/index.js'
import {
  autocompletePhysicalLocationsGQLQuery,
  cancelEventRegistrationGQLMutation,
  createEventGQLQuery,
  deleteEventGQLQuery,
  eventRegistrationsGQLQuery,
  generateStaticMapUrlGQLQuery,
  eventGQLQuery,
  eventsGQLQuery,
  getPlaceDetailsGQLQuery,
  getHasOngoingEventsGQLQuery,
  registerForEventGQLMutation,
  searchEventForEmbedGQLQuery,
  updateEventGQLQuery,
  eventRecurrencesGQLQuery,
  getCalendarInviteDataGQLQuery,
  getEventAnalyticsGQLQuery,
} from '../../graphql/post/events.gql.js'
import {
  ActionFields,
  AutocompletePhysicalLocationResultFields,
  EventRecurrenceFields,
  EventRegistrationFields,
  PlaceDetailsFields,
} from '../../types/fields.generated.js'
import {
  Event,
  MutationCancelEventRegistrationArgs,
  MutationCreateEventArgs,
  MutationDeleteEventArgs,
  MutationRegisterForEventArgs,
  MutationUpdateEventArgs,
  PaginatedEvent,
  PaginatedEventRegistration,
  QueryEventRegistrationsArgs,
  Action,
  QueryAutocompletePhysicalLocationsArgs,
  AutocompletePhysicalLocationResult,
  PlaceDetails,
  QueryGetPlaceDetailsArgs,
  QueryGenerateStaticMapUrlArgs,
  StaticMaps,
  PaginatedEventRecurrence,
  QueryEventRecurrencesArgs,
  QueryEventArgs,
  QueryEventsArgs,
  QueryCalendarInviteDataArgs,
  CalendarInviteData,
  AnalyticsResponse,
  QueryEventAnalyticsArgs,
} from '../../types/index.js'
import { GraphqlClient } from '../graphql.client.js'

export class EventsClient {
  private client: GraphqlClient

  constructor(client: GraphqlClient) {
    this.client = client
  }

  async get(variables: QueryEventArgs, accessToken?: string): Promise<Event> {
    type QueryResult = { event: Event }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: eventGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.event
  }

  async create(
    variables: MutationCreateEventArgs,
    accessToken?: string,
  ): Promise<Event> {
    type QueryResult = { createEvent: Event }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: createEventGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.createEvent
  }

  async update(
    variables: MutationUpdateEventArgs,
    accessToken?: string,
  ): Promise<Event> {
    type QueryResult = { updateEvent: Event }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: updateEventGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.updateEvent
  }

  async delete(
    variables: MutationDeleteEventArgs,
    fields: ActionFields = 'basic',
    accessToken?: string,
  ): Promise<Action> {
    type QueryResult = { deleteEvent: Action }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: deleteEventGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.deleteEvent
  }

  async list(
    variables: QueryEventsArgs,
    accessToken?: string,
  ): Promise<PaginatedEvent> {
    type QueryResult = { events: PaginatedEvent }

    const result = await this.client.authorizedRequest<QueryResult>({
      query: eventsGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.events
  }

  async hasOngoingEvents(
    variables: QueryEventsArgs,
    accessToken?: string,
  ): Promise<PaginatedEvent> {
    type QueryResult = { events: PaginatedEvent }

    const result = await this.client.authorizedRequest<QueryResult>({
      query: getHasOngoingEventsGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.events
  }

  async searchEventForEmbed(
    variables: QueryEventsArgs,
    accessToken?: string,
  ): Promise<PaginatedEvent> {
    type QueryResult = { events: PaginatedEvent }

    const result = await this.client.authorizedRequest<QueryResult>({
      query: searchEventForEmbedGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.events
  }

  async registerForEvent(
    variables: MutationRegisterForEventArgs,
    fields: ActionFields = 'basic',
    accessToken?: string,
  ): Promise<Action> {
    type QueryResult = { action: Action }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: registerForEventGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.action
  }

  async cancelEventRegistration(
    variables: MutationCancelEventRegistrationArgs,
    fields: ActionFields = 'basic',
    accessToken?: string,
  ): Promise<Action> {
    type QueryResult = { action: Action }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: cancelEventRegistrationGQLMutation(fields),
      variables,
      customToken: accessToken,
    })
    return result.action
  }

  async eventRegistrations(
    variables: QueryEventRegistrationsArgs,
    fields: EventRegistrationFields = 'basic',
    accessToken?: string,
  ): Promise<PaginatedEventRegistration> {
    type QueryResult = { eventRegistrations: PaginatedEventRegistration }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: eventRegistrationsGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.eventRegistrations
  }

  async autocompletePhysicalLocations(
    variables: QueryAutocompletePhysicalLocationsArgs,
    fields: AutocompletePhysicalLocationResultFields = 'basic',
    accessToken?: string,
  ): Promise<AutocompletePhysicalLocationResult> {
    type QueryResult = {
      autocompletePhysicalLocations: AutocompletePhysicalLocationResult
    }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: autocompletePhysicalLocationsGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.autocompletePhysicalLocations
  }

  async getPlaceDetails(
    variables: QueryGetPlaceDetailsArgs,
    fields?: PlaceDetailsFields,
    accessToken?: string,
  ): Promise<PlaceDetails> {
    type QueryResult = { getPlaceDetails: PlaceDetails }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getPlaceDetailsGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.getPlaceDetails
  }

  async generateStaticMapUrl(
    variables: QueryGenerateStaticMapUrlArgs,
    accessToken?: string,
  ): Promise<StaticMaps> {
    type QueryResult = { generateStaticMapUrl: StaticMaps }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: generateStaticMapUrlGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.generateStaticMapUrl
  }

  async eventRecurrences(
    variables: QueryEventRecurrencesArgs,
    fields?: EventRecurrenceFields,
    accessToken?: string,
  ): Promise<PaginatedEventRecurrence> {
    type QueryResult = { eventRecurrences: PaginatedEventRecurrence }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: eventRecurrencesGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.eventRecurrences
  }

  async getCalendarInviteData(
    variables: QueryCalendarInviteDataArgs,
    accessToken?: string,
  ): Promise<CalendarInviteData> {
    type QueryResult = { calendarInviteData: CalendarInviteData }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getCalendarInviteDataGQLQuery(),
      variables,
      customToken: accessToken,
    })
    return result.calendarInviteData
  }

  async eventAnalytics(
    variables: QueryEventAnalyticsArgs,
    fields?: ReportRecordFields,
    accessToken?: string,
  ): Promise<AnalyticsResponse> {
    type QueryResult = { eventAnalytics: AnalyticsResponse }
    const result = await this.client.authorizedRequest<QueryResult>({
      query: getEventAnalyticsGQLQuery(fields),
      variables,
      customToken: accessToken,
    })
    return result.eventAnalytics
  }
}
